import React from 'react';
import { gsap } from 'gsap';

const FlowingMenu = ({ items = [] }) => {
  return (
    <div className="w-full h-full overflow-hidden">
      <nav className="flex flex-col h-full m-0 p-0">
        {items.map((item, idx) => (
          <MenuItem key={idx} {...item} />
        ))}
      </nav>
    </div>
  );
};

const MenuItem = ({ link = '#', title, description, icon, image }) => {
  const itemRef = React.useRef(null);
  const marqueeRef = React.useRef(null);
  const marqueeInnerRef = React.useRef(null);

  const animationDefaults = { duration: 0.6, ease: 'expo' };

  const findClosestEdge = (mouseX, mouseY, width, height) => {
    const topEdgeDist = Math.pow(mouseX - width / 2, 2) + Math.pow(mouseY, 2);
    const bottomEdgeDist = Math.pow(mouseX - width / 2, 2) + Math.pow(mouseY - height, 2);
    return topEdgeDist < bottomEdgeDist ? 'top' : 'bottom';
  };

  const handleMouseEnter = (ev) => {
    if (!itemRef.current || !marqueeRef.current || !marqueeInnerRef.current) return;
    const rect = itemRef.current.getBoundingClientRect();
    const edge = findClosestEdge(ev.clientX - rect.left, ev.clientY - rect.top, rect.width, rect.height);

    const tl = gsap.timeline({ defaults: animationDefaults });
    tl.set(marqueeRef.current, { y: edge === 'top' ? '-101%' : '101%' })
      .set(marqueeInnerRef.current, { y: edge === 'top' ? '101%' : '-101%' })
      .to([marqueeRef.current, marqueeInnerRef.current], { y: '0%' });
  };

  const handleMouseLeave = (ev) => {
    if (!itemRef.current || !marqueeRef.current || !marqueeInnerRef.current) return;
    const rect = itemRef.current.getBoundingClientRect();
    const edge = findClosestEdge(ev.clientX - rect.left, ev.clientY - rect.top, rect.width, rect.height);

    const tl = gsap.timeline({ defaults: animationDefaults });
    tl.to(marqueeRef.current, { y: edge === 'top' ? '-101%' : '101%' })
      .to(marqueeInnerRef.current, { y: edge === 'top' ? '101%' : '-101%' }, '<');
  };

  const repeatedMarqueeContent = React.useMemo(() => {
    return Array.from({ length: 4 }).map((_, idx) => (
      <React.Fragment key={idx}>
        <span className="text-white uppercase font-bold text-[3vh] md:text-[4vh] leading-[1.2] p-[1vh_1vw_0] drop-shadow-md">
          {title}
        </span>
        {image && (
          <div 
            className="w-[120px] h-[120px] md:w-[150px] md:h-[150px] my-[1em] mx-[2vw] rounded-2xl bg-cover bg-center shadow-2xl border-4 border-white"
            style={{ backgroundImage: `url(${image})` }}
          />
        )}
        {description && (
          <span className="text-white/95 text-sm md:text-base px-4 max-w-[250px] font-medium">
            {description}
          </span>
        )}
      </React.Fragment>
    ));
  }, [title, image, description]);

  return (
    <div 
      className="flex-1 relative overflow-hidden text-center border-b border-gray-200 last:border-b-0 hover:bg-gray-50/50 transition-colors duration-300" 
      ref={itemRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <a
        className="flex items-center justify-center h-full relative cursor-pointer uppercase no-underline font-semibold text-steel text-[3vh] md:text-[4vh] hover:text-primary focus:text-steel focus-visible:text-primary transition-colors duration-300 bg-white md:pointer-events-auto pointer-events-none"
        href={link}
        onClick={(e) => {
          if (window.innerWidth < 768) {
            e.preventDefault();
          }
        }}
      >
        {title}
      </a>
      <div
        className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none bg-gradient-to-br from-primary via-primary to-primary-light translate-y-[101%]"
        ref={marqueeRef}
      >
        <div className="h-full w-[200%] flex" ref={marqueeInnerRef}>
          <div className="flex items-center relative h-full w-[200%] will-change-transform animate-marquee">
            {repeatedMarqueeContent}
          </div>
        </div>
      </div>
    </div>
  );
};

export { FlowingMenu };
export default FlowingMenu;
